/**
 * OFFICIALS_PACK wizard — admin creates a Match-Officials bundle by
 * picking already-ingested umpires + referees from the SYSTEM catalog.
 * Backend copies each pick into a PackUmpire template row; on purchase,
 * the existing _clone_pack_contents path clones into the buyer's tenant
 * as USER-owned Umpire rows.
 *
 * Opens when /admin/marketplace mounts with ?action=new&type=OFFICIALS_PACK.
 *
 * 2026-07-18a — CSV upload → catalog picker.
 * 2026-07-18b — full-screen layout via WizardShell (sidebar + main).
 */
import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Gavel, Save } from "lucide-react";
import {
  createPack,
  updatePack,
  getPack,
  listCatalogUmpires,
  selectOfficialsFromCatalog,
} from "../../api/owner.api";
import CatalogPicker from "../../components/marketplace/CatalogPicker";
import PackDetailsForm from "../../components/marketplace/PackDetailsForm";
import WizardShell from "../../components/marketplace/WizardShell";
import UnmatchedNotice from "../../components/marketplace/UnmatchedNotice";
import { detailsFromPack, hydrateOfficials } from "../../components/marketplace/packHydrate";

const ROLE_TONE = {
  UMPIRE:  "bg-sky-500/15 text-sky-300 border-sky-400/30",
  REFEREE: "bg-amber-500/15 text-amber-300 border-amber-400/30",
};

export default function OfficialsPackWizard({ onClose, onCreated, editPackId = null }) {
  const [details, setDetails] = useState({
    name: "",
    season: "",
    short_description: "",
    description: "",
    price_credits: 49,
  });
  const [umpireIds, setUmpireIds] = useState([]);
  // Cache the picked rows so the sidebar counter can show umpires vs refs
  // without a second call.
  const [pickedById, setPickedById] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // 2026-07-31 — Publish belongs to TOURNAMENT_BUNDLE only; see
  // SquadPackWizard for the shared rationale.
  const [packId, setPackId] = useState(editPackId);
  const [savedAt, setSavedAt] = useState(null);
  // 2026-08-03 — edit mode; see SquadPackWizard for the shared rationale.
  const [hydrating, setHydrating] = useState(Boolean(editPackId));
  const [unmatched, setUnmatched] = useState([]);
  const [seedRows, setSeedRows] = useState([]);
  const isEdit = Boolean(editPackId);

  useEffect(() => {
    if (!editPackId) return;
    let cancelled = false;
    (async () => {
      setHydrating(true);
      setError(null);
      try {
        const { data } = await getPack(editPackId);
        if (cancelled) return;
        const h = hydrateOfficials(data);
        setDetails(detailsFromPack(data));
        setUmpireIds(h.ids);
        setSeedRows(h.seedRows);
        setUnmatched(h.unmatched);
        // Seed the sidebar umpire/referee split without a second call.
        const byId = {};
        for (const r of h.seedRows) byId[String(r.id)] = r;
        setPickedById(byId);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message
            || "Could not load this draft.");
        }
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editPackId]);

  function handleUmpireToggle(row, nowOn) {
    if (nowOn) setPickedById((p) => ({ ...p, [String(row.id)]: row }));
  }

  const { umpireCount, refereeCount } = useMemo(() => {
    let u = 0, r = 0;
    for (const id of umpireIds) {
      const row = pickedById[String(id)];
      if (row?.role === "REFEREE") r++; else u++;
    }
    return { umpireCount: u, refereeCount: r };
  }, [umpireIds, pickedById]);

  function detailsValid() {
    return details.name.trim().length >= 3
      && Number.isFinite(Number(details.price_credits))
      && Number(details.price_credits) >= 0;
  }

  function _packBody() {
    return {
      name: details.name.trim(),
      season: details.season.trim(),
      short_description: details.short_description.trim(),
      description: details.description.trim(),
      price_credits: Number(details.price_credits),
    };
  }

  async function _persist() {
    let pack;
    if (packId) {
      const { data } = await updatePack(packId, _packBody());
      pack = data;
    } else {
      const slug = details.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80);
      const { data } = await createPack({
        ..._packBody(),
        slug,
        pack_type: "OFFICIALS_PACK",
      });
      pack = data;
      setPackId(pack.id);
    }
    const { data: summary } = await selectOfficialsFromCatalog(pack.id, umpireIds);
    return { pack, summary };
  }

  function _errorMessage(e) {
    return e?.response?.data
      ? (typeof e.response.data === "string"
          ? e.response.data
          : (e.response.data.detail
              || e.response.data.error
              || JSON.stringify(e.response.data)))
      : e.message;
  }

  function _validate() {
    if (!detailsValid()) {
      setError("Fill in name (≥ 3 chars) and a non-negative price first.");
      return false;
    }
    if (umpireIds.length === 0) {
      setError("Pick at least one official from the catalog.");
      return false;
    }
    return true;
  }

  async function handleSave() {
    if (!_validate()) return;
    setBusy(true);
    setError(null);
    try {
      const { pack } = await _persist();
      setSavedAt(new Date());
      if (onCreated) onCreated(pack);
    } catch (e) {
      setError(_errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const sidebar = (
    <>
      <PackDetailsForm
        details={details}
        setDetails={setDetails}
        namePlaceholder="ICC Elite Panel — Umpires & Referees 2026"
        shortDescriptionPlaceholder="ICC Elite Panel — 12 umpires + 4 referees"
      />
      <div className="pt-3 border-t border-border-default text-xs text-text-secondary">
        <div className="flex items-center gap-1.5 text-text-primary font-medium mb-1">
          <Gavel className="w-3.5 h-3.5" /> {umpireIds.length} official{umpireIds.length === 1 ? "" : "s"} picked
        </div>
        {umpireIds.length > 0 && (
          <div className="ml-5">{umpireCount} umpire{umpireCount === 1 ? "" : "s"} · {refereeCount} referee{refereeCount === 1 ? "" : "s"}</div>
        )}
      </div>

      {savedAt && (
        <div className="pt-3 border-t border-border-default text-xs text-emerald-300 flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Draft saved · {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </>
  );

  const isSaved = Boolean(packId);
  return (
    <WizardShell
      eyebrow="Marketplace · OFFICIALS_PACK"
      title={isEdit ? "Edit match-officials pack" : "New match-officials pack"}
      onClose={onClose}
      busy={busy || hydrating}
      error={error}
      sidebar={sidebar}
      primaryLabel={isSaved ? "Update draft" : "Save draft"}
      primaryIcon={<Save className="w-4 h-4" />}
      primaryDisabled={hydrating || !detailsValid() || umpireIds.length === 0}
      primaryTone="sky"
      onPrimary={handleSave}
    >
      <section className="space-y-3">
          {hydrating && (
            <div className="rounded-md border border-border-default bg-bg-secondary/40 px-3 py-6 text-sm text-text-secondary text-center">
              Loading saved draft…
            </div>
          )}

          <UnmatchedNotice items={unmatched} />
          <div>
            <h3 className="text-base font-semibold">Pick officials from the catalog</h3>
            <p className="text-sm text-text-secondary mt-0.5">
              Only SYSTEM-owned umpires and referees the platform has already
              ingested are shown. Search by name or city.
            </p>
          </div>
          <CatalogPicker
            fetchFn={(params) => listCatalogUmpires(params)}
            selectedIds={umpireIds}
            seedRows={seedRows}
            onChange={setUmpireIds}
            onToggle={handleUmpireToggle}
            placeholder="Search umpires and referees…"
            emptyHint="No officials match. Try a different search."
            renderRow={(u) => (
              <div className="flex items-baseline justify-between gap-2">
                <div className="min-w-0 flex items-center gap-2">
                  <span className="font-medium">{u.name}</span>
                  {u.role && (
                    <span className={`text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${ROLE_TONE[u.role] || ""}`}>
                      {u.role === "REFEREE" ? "REF" : "UMP"}
                    </span>
                  )}
                </div>
                <div className="text-xs text-text-secondary shrink-0">
                  {[u.city, u.nationality_name].filter(Boolean).join(" · ")}
                </div>
              </div>
            )}
            renderChip={(u) => (
              <span>
                {u.name}{u.role === "REFEREE" ? " (ref)" : ""}
              </span>
            )}
          />
      </section>
    </WizardShell>
  );
}
